import { useCallback, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'

type ClickToCopyProps = {
  value: string
  className?: string
  title?: string
  ariaLabel?: string
  children?: ReactNode
  onCopy?: () => void
}

export function ClickToCopy({
  value,
  className = '',
  title,
  ariaLabel,
  children,
  onCopy,
}: ClickToCopyProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    if (!value) {
      return
    }
    void navigator.clipboard.writeText(value)
    setCopied(true)
    if (onCopy) {
      onCopy()
    }
    requestAnimationFrame(() => {
      setCopied(false)
    })
  }, [onCopy, value])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLSpanElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        handleCopy()
      }
    },
    [handleCopy],
  )

  const classes = [className, copied ? 'copied' : null].filter(Boolean).join(' ')
  const label = ariaLabel ?? title ?? 'Click to copy'
  const tooltip = title ?? 'Click to copy'

  return (
    <span
      role="button"
      tabIndex={0}
      className={classes}
      onClick={handleCopy}
      onKeyDown={handleKeyDown}
      title={tooltip}
      aria-label={label}
    >
      {children ?? value}
    </span>
  )
}
